import moment from 'moment';
import {objectToQueryString, removeNullish} from '../../utils/objects';
import {getFirstSchedulableDay} from '../../utils/scheduled-window';
import {snakeCaseKeys} from '../../utils/snake-case-keys';
import {api} from './apiSlice';

export const scheduleWindowsApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * getSchedulableWindows
     * * Takes a currentDay ISOString, neededDays number, repairData object, and selectedDay ISOString
     * * Returns errors, precise, selectedDay, minimumDay, maximumDay, and windows
     */
    getSchedulableWindows: build.query({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const {neededDays = 14, repairData = {}, targetDay = moment().toISOString()} = arg;
        const currentDay = moment().toISOString();
        const {group, repair} = repairData;
        const {latitude = null, longitude = null, timeZone = ''} = repair;
        const productIds = {[repair?.product?.id]: repair.issues};
        const certificationIds = group.certificationOptions.filter(({value}) => value).map(({id}) => id);
        const params = removeNullish({
          certificationIds,
          claimId: repair.id,
          groupId: group.id,
          latitude,
          longitude,
          neededDays,
          productIds,
          target: targetDay,
          timeZone,
          storeId: repair.storeLookupCode,
        });
        const query = objectToQueryString(snakeCaseKeys(params));
        const getSchedulableWindowsResults = await fetchWithBaseQuery(`/schedule/calculate?${query}`);
        if (getSchedulableWindowsResults.error) return {data: {windows: {}, precise: false, errors: []}};

        const {windows = {}, precise = false, errors = []} = getSchedulableWindowsResults.data;
        const maximumDay = null;
        const newMomentWindows = mapWindows(windows, maximumDay);
        const newMergedWindows = Object.assign({}, {}, newMomentWindows);

        let minimumDay = Object.keys(newMergedWindows).length
          ? getFirstSchedulableDay(newMergedWindows, moment(currentDay))
          : currentDay;
        let newSelectedDay;
        if (moment(targetDay).isBefore(minimumDay, 'day')) {
          newSelectedDay = minimumDay;
        }
        newSelectedDay = newSelectedDay ? moment(newSelectedDay).toISOString() : targetDay;

        return {
          data: {
            maximumDay: maximumDay ? maximumDay.toISOString() : null,
            minimumDay: minimumDay ? minimumDay.toISOString() : null,
            precise,
            selectedDay: newSelectedDay,
            windows: newMergedWindows,
            errors,
          },
        };
      },
      extraOptions: {maxRetries: 2},
      serializeQueryArgs: ({endpointName}) => {
        return endpointName;
      },
      forceRefetch({currentArg, previousArg}) {
        return currentArg !== previousArg;
      },
      merge: (currentCache, newItems) => {
        currentCache.windows = {...currentCache.windows, ...newItems.windows};
      },
    }),
  }),
  overrideExisting: false,
});

function mapWindows(schedulableWindows, maxDate) {
  return Object.keys(schedulableWindows).reduce((windows, dayString) => {
    if (maxDate && maxDate < moment(dayString)) {
      return windows;
    }

    windows[dayString] = schedulableWindows[dayString].map(({start, end, available, time_zone, appointment_id}) => ({
      start: moment(start).toISOString(),
      end: moment(end).toISOString(),
      available,
      utcOffset: moment.parseZone(start).utcOffset(),
      timeZone: time_zone,
      appointmentId: appointment_id,
    }));
    return windows;
  }, {});
}

export const {useGetSchedulableWindowsQuery} = scheduleWindowsApi;
